import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faPlus, faEdit, faTrash, faSpinner,
  faExclamationTriangle, faCheckCircle, faTimesCircle,
  faClock, faPlay, faPause, faHistory,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

const EmailRouterList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchConfigs(); }, []);  // eslint-disable-line

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const resp = await fetch('/api/email-router/configs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to load inbox configurations');
      const data = await resp.json();
      setConfigs(data.configs || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete inbox "${name}"? This removes all routing rules and credentials.`)) return;
    try {
      const resp = await fetch(`/api/email-router/configs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Delete failed');
      fetchConfigs();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 12, color: '#5b8def' }} />
            Email Router
          </h1>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => navigate('/email-router/history')}
              style={btnSecondary}
            >
              <FontAwesomeIcon icon={faHistory} /> History
            </button>
            <button
              onClick={() => navigate('/email-router/new')}
              style={btnPrimary}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Inbox
            </button>
          </div>
        </div>

        <p style={{ color: '#666', marginBottom: 24 }}>
          Connect inboxes, define routing rules, and let incoming emails be auto-dispatched to the right agent workflow.
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading…
          </div>
        )}

        {error && (
          <div style={errorBox}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}

        {!loading && !error && configs.length === 0 && (
          <div style={emptyState}>
            <FontAwesomeIcon icon={faEnvelope} size="3x" style={{ color: '#bbb', marginBottom: 16 }} />
            <h3>No inboxes configured yet</h3>
            <p style={{ color: '#666' }}>
              Click <b>+ Add Inbox</b> to connect your first inbox and define routing rules.
            </p>
          </div>
        )}

        {!loading && !error && configs.length > 0 && (
          <div style={tableWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7f9fc', textAlign: 'left' }}>
                  <th style={th}>Name</th>
                  <th style={th}>Inbox</th>
                  <th style={th}>Auth</th>
                  <th style={th}>Rules</th>
                  <th style={th}>Status</th>
                  <th style={th}>Last Poll</th>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id} style={{ borderTop: '1px solid #eef0f4' }}>
                    <td style={td}><b>{c.name}</b></td>
                    <td style={td}>{c.inbox_email}</td>
                    <td style={td}>
                      {c.credentials_status?.connected ? (
                        <span style={pill('#e8f5ee', '#1f8a3a')}>
                          {c.credentials_status.auth_mode === 'sso' ? 'SSO' : 'App Creds'}
                        </span>
                      ) : (
                        <span style={pill('#fff4e6', '#a86a00')}>Not connected</span>
                      )}
                    </td>
                    <td style={td}>{c.category_count}</td>
                    <td style={td}>
                      {c.enabled
                        ? <span style={{ color: '#1f8a3a' }}><FontAwesomeIcon icon={faPlay} /> Live</span>
                        : <span style={{ color: '#888' }}><FontAwesomeIcon icon={faPause} /> Paused</span>}
                    </td>
                    <td style={td}>
                      {c.last_polled_at
                        ? <span style={{ color: '#666' }}><FontAwesomeIcon icon={faClock} /> {new Date(c.last_polled_at).toLocaleString()}</span>
                        : <span style={{ color: '#bbb' }}>Never</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        title="Edit"
                        onClick={() => navigate(`/email-router/edit/${c.id}`)}
                        style={iconBtn}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDelete(c.id, c.name)}
                        style={{ ...iconBtn, color: '#c0392b' }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

// --- inline styles ---
const btnPrimary = {
  background: '#5b8def', color: 'white', border: 'none', padding: '10px 18px',
  borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const btnSecondary = {
  background: 'white', color: '#5b8def', border: '1px solid #5b8def',
  padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const tableWrap = {
  background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
};
const th = { padding: '12px 16px', fontSize: 13, color: '#555', fontWeight: 600 };
const td = { padding: '14px 16px', fontSize: 14, color: '#222' };
const iconBtn = {
  background: 'transparent', border: 'none', padding: '6px 10px',
  cursor: 'pointer', color: '#555', marginLeft: 4, fontSize: 14,
};
const errorBox = {
  background: '#fdecec', color: '#c0392b', padding: 16, borderRadius: 6, marginBottom: 16,
};
const emptyState = {
  textAlign: 'center', padding: 48, background: 'white', borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const pill = (bg, fg) => ({
  background: bg, color: fg, padding: '4px 10px', borderRadius: 12,
  fontSize: 12, fontWeight: 600,
});

export default EmailRouterList;
